import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Check,
  ChevronDown,
  Clock,
  DollarSign,
  GraduationCap,
  Lightbulb,
  MessageCircle,
  Mic,
  Shield,
  Trophy,
  Users,
  Video,
  Zap,
} from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { trackEvent } from '../firebase';
import { cn } from '../util/designUtil';

const LandingScreen: React.FC = () => {
  const { theme } = useColorTheme();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleCtaClick = (ctaName: string, placement: string) => {
    void trackEvent('landing_cta_click', {
      cta_name: ctaName,
      placement,
    });
  };

  const faqs = [
    {
      question: "What is the minimum call duration?",
      answer: "The minimum call duration is 1 minute. You only pay for the time you actually spend on the call.",
    },
    {
      question: "How does billing work?",
      answer: "Nexbii uses a pay-per-minute pricing model. Each creator sets their own rate, and you're charged based on the duration of your session. Payment is processed securely before the session begins.",
    },
    {
      question: "How do I become a creator on Nexbii?",
      answer: "Simply sign up for an account, verify your social media profiles, set your availability and per-minute rate, and you're ready to start accepting bookings from fans.",
    },
    {
      question: "Is my payment information secure?",
      answer: "Yes, all payments are handled through secure, encrypted payment processing. We never store your full payment details on our servers.",
    },
    {
      question: "Can I cancel or reschedule a booking?",
      answer: "Yes, you can cancel a booking up to 24 hours before the scheduled session for a full refund. To reschedule, cancel the existing booking and create a new one.",
    },
    {
      question: "What do I need for a video call?",
      answer: "You need a device with a camera and microphone (computer, tablet, or smartphone) and a stable internet connection. We recommend using Google Chrome for the best experience.",
    },
  ];

  const features = [
    {
      icon: Video,
      title: "Live Video Calls",
      description: "Connect face-to-face with your favorite creators in high-quality video sessions.",
    },
    {
      icon: DollarSign,
      title: "Pay Per Minute",
      description: "Fair and transparent pricing. Only pay for the time you spend connecting.",
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Book sessions that fit your schedule. Choose from available time slots.",
    },
    {
      icon: MessageCircle,
      title: "Real-Time Chat",
      description: "Engage in meaningful conversations with instant messaging support.",
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your conversations are protected with end-to-end encryption.",
    },
    {
      icon: Users,
      title: "Verified Creators",
      description: "All creators are verified to ensure authentic and quality interactions.",
    },
  ];

  const benefits = [
    {
      title: "For People Looking for Access",
      items: [
        "Get personalised advice and insights",
        "Speak directly with experts and creators",
        "Ask questions that matter to you",
        "Learn, explore, or get guidance",
        "Build meaningful connections",
      ],
    },
    {
      title: "For Creators & Experts",
      items: [
        "Monetise your time, knowledge, or audience",
        "Offer 1:1 sessions or consultations",
        "Set your own rates",
        "Manage your availability easily",
        "Build deeper relationships",
      ],
    },
  ];

  const audiences = [
    {
      icon: Mic,
      title: "Creators & Influencers",
    },
    {
      icon: Lightbulb,
      title: "Coaches & Mentors",
    },
    {
      icon: GraduationCap,
      title: "Educational Consultants",
    },
    {
      icon: Briefcase,
      title: "Industry Experts",
    },
    {
      icon: Trophy,
      title: "Athletes & Public Figures",
    },
    {
      icon: Users,
      title: "Freelancers with an audience",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Create Your Account",
      description: "Sign up in seconds and set up your profile. It's completely free to get started.",
    },
    {
      number: "02",
      title: "Browse Creators",
      description: "Explore our diverse community of creators across all categories and interests.",
    },
    {
      number: "03",
      title: "Book a Session",
      description: "Choose a time slot that works for you and book your personalized session.",
    },
    {
      number: "04",
      title: "Connect & Engage",
      description: "Join your scheduled session and enjoy meaningful one-on-one time.",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30">
      {/* Hero Section */}
      <section className="relative w-full py-10 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 to-pink-100/20" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />

        <div className="container px-8 md:px-3 relative z-10 mx-auto">
          <div className="flex flex-col items-center text-center space-y-6 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-full border-2 border-purple-200 shadow-lg">
              <Zap className={cn("h-4 w-4", theme.secondary)} />
              <span className="text-sm font-semibold text-gray-700">Direct Access to People Who Matter</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-7xl font-bold tracking-tight">
              Get Direct Access to
              <br />
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>
                People Who Matter
              </span>{" "}
              to You
            </h1>

            <p className="text-base sm:text-lg md:text-2xl text-gray-600 max-w-2xl leading-relaxed">
              Creators, experts, coaches, and public figures - all in one place. Nexbii lets you book one-on-one
              video sessions with people you want to learn from, get advice from, or connect with. Pay per minute,
              ask what matters, and build real connections.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2 w-full sm:w-auto">
              <Link to="/register" onClick={() => handleCtaClick('get_started_free', 'hero')}>
                <button
                  className={cn(
                    "w-full sm:w-auto px-6 py-4 sm:px-10 sm:py-6 text-base sm:text-lg font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all bg-gradient-to-r text-white",
                    theme.primary,
                  )}
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5 inline" />
                </button>
              </Link>
              <Link to="/login" onClick={() => handleCtaClick('sign_in', 'hero')}>
                <button className="w-full sm:w-auto px-6 py-4 sm:px-10 sm:py-6 text-base sm:text-lg font-semibold rounded-2xl border-2 hover:shadow-lg bg-transparent hover:bg-gray-50 transition-all">
                  Sign In
                </button>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full py-16 bg-white">
        <div className="container px-8 md:px-10 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              Everything You Need for
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>
                {" "}
                Meaningful Connections
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to make every interaction valuable, secure, and memorable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 group rounded-lg bg-white p-8"
              >
                <div
                  className={cn(
                    "inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br mb-6 group-hover:scale-110 transition-transform",
                    theme.primary,
                  )}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="w-full py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              How <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>Nexbii</span>{" "}
              Works
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in minutes and begin connecting with creators you admire.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative flex flex-col md:flex-row gap-8 mb-12 last:mb-0">
                {index < steps.length - 1 && (
                  <div className="absolute left-[31px] top-20 bottom-0 w-0.5 bg-gradient-to-b from-purple-300 to-pink-300 hidden md:block" />
                )}
                <div className="relative flex-shrink-0">
                  <div
                    className={cn(
                      "w-16 h-16 rounded-2xl bg-gradient-to-br flex items-center justify-center text-2xl font-bold text-white shadow-lg",
                      theme.primary,
                    )}
                  >
                    {step.number}
                  </div>
                </div>
                <div className="flex-1 border-2 border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all rounded-lg bg-white p-5 sm:p-8">
                  <h3 className="text-lg sm:text-2xl font-bold mb-3 text-gray-900">{step.title}</h3>
                  <p className="text-sm sm:text-lg text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full py-16 bg-white">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              Built For{" "}
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>
                Different Ways to Connect
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you're seeking access or offering expertise, Nexbii has everything you need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="border-2 border-gray-100 hover:shadow-xl transition-all rounded-lg bg-white p-8">
                <div
                  className={cn(
                    "inline-flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r text-white mb-6 font-semibold",
                    theme.primary,
                  )}
                >
                  {benefit.title}
                </div>
                <ul className="space-y-4">
                  {benefit.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <div
                        className={cn(
                          "flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center mt-0.5",
                          theme.primary,
                        )}
                      >
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section className="w-full py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              Who Is{" "}
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>Nexbii</span>{" "}
              For?
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Nexbii is designed for anyone who wants to share their expertise or gain access to knowledgeable people.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5 max-w-6xl mx-auto">
            {audiences.map((audience, index) => (
              <div
                key={index}
                className="border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 rounded-lg bg-white px-5 py-7 md:px-6 md:py-8 text-center group"
              >
                <div
                  className={cn(
                    "inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br mb-5 group-hover:scale-110 transition-transform",
                    theme.primary,
                  )}
                >
                  <audience.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-[1.05rem] md:text-lg font-bold text-gray-900 leading-[1.45]">{audience.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="w-full py-16 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              Simple,{" "}
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>Transparent</span>{" "}
              Pricing
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Pay only for the time you use. No subscriptions, no hidden fees.
            </p>
          </div>

          <div className="max-w-3xl mx-auto border-2 border-purple-200 shadow-2xl rounded-lg bg-white p-6 sm:p-10">
            <div className="text-center mb-8">
              <div
                className={cn(
                  "text-3xl sm:text-5xl md:text-6xl font-bold mb-2 bg-gradient-to-r bg-clip-text text-transparent",
                  theme.primary,
                )}
              >
                Per-Minute Pricing
              </div>
              <div className="text-base sm:text-xl text-gray-600">Set by each individual creator or expert</div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Check className={cn("h-6 w-6 flex-shrink-0", theme.secondary)} />
                <span className="text-gray-700">Rates based on experience, demand, or audience</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Check className={cn("h-6 w-6 flex-shrink-0", theme.secondary)} />
                <span className="text-gray-700">Minimum session length: 1 minute</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Check className={cn("h-6 w-6 flex-shrink-0", theme.secondary)} />
                <span className="text-gray-700">Secure payment processing</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Check className={cn("h-6 w-6 flex-shrink-0", theme.secondary)} />
                <span className="text-gray-700">Cancel up to 24 hours before for a full refund</span>
              </div>
            </div>

            <Link to="/register" className="block" onClick={() => handleCtaClick('start_connecting_today', 'pricing')}>
              <button
                className={cn(
                  "w-full py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all bg-gradient-to-r text-white",
                  theme.primary,
                )}
              >
                Start Connecting Today
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="w-full py-16 bg-white">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
              Frequently{" "}
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}>
                Asked Questions
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about Nexbii.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border-2 border-gray-100 rounded-lg overflow-hidden hover:border-purple-200 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm sm:text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200",
                      openFaq === index && "rotate-180"
                    )}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-16 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
        <div className="container px-8 md:px-4 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Journey?</h2>
            <p className="text-base sm:text-xl mb-10 opacity-90 leading-relaxed">
              Join thousands of fans and creators already connecting on Nexbii. Whether you're looking to learn, share,
              or simply connect, we've got you covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" onClick={() => handleCtaClick('create_your_account', 'final_cta')}>
                <button className="w-full sm:w-auto px-6 py-4 sm:px-10 sm:py-6 text-base sm:text-lg font-semibold rounded-2xl bg-white text-purple-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all">
                  Create Your Account
                  <ArrowRight className="ml-2 h-5 w-5 inline" />
                </button>
              </Link>
              <Link to="/login" onClick={() => handleCtaClick('sign_in_now', 'final_cta')}>
                <button className="w-full sm:w-auto px-6 py-4 sm:px-10 sm:py-6 text-base sm:text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white/10 bg-transparent">
                  Sign In Now
                </button>
              </Link>
            </div>

            <div className="mt-12 pt-12 border-t border-white/20">
              <p className="text-sm opacity-75">
                Have questions? Check out our{" "}
                <a href="#faq" className="underline hover:opacity-100">
                  FAQ
                </a>{" "}
                or{" "}
                <a href="mailto:info@nexbii.com" className="underline hover:opacity-100">
                  contact our support team
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingScreen;
